import * as React from 'react';

import ErrorBoundary from '../../components/ErrorBoundary';
import { cn } from '../../lib/utils';
import type { Feature } from '../features';
import { PREVIEW_BLUR, PREVIEW_CLAMP, PREVIEW_HIDE_CONTROLS } from './previewChrome';
import { UpsellCard, useUpsellSuppressed } from './UpsellCard';
import { useInert } from './useInert';

type Props = {
  feature: Feature;
  /**
   * The real panel, rendered blurred behind the card.
   *
   * Pass it ONLY when it would render the user's actual data. Two failure modes to avoid:
   * a blurred *fabricated* chart is a marketing screen with a filter on it, and a blurred
   * *empty state* is a fuzzy grey box — both are worse than no preview, and an analyst at
   * a large agency will spot the first one. Previews are expected to self-suppress
   * (render `null`) when they have nothing real, which degrades to the centred card.
   */
  preview?: React.ReactNode;
  title?: string;
  description?: string;
  className?: string;
};

/**
 * What a locked panel degrades to in presenter mode.
 *
 * Everything commercial is gone — no sparkle, no "Nexoya Premium", no price, no CTA — but the
 * panel still says something, because a blank white area in the middle of an agency's own
 * client presentation is worse for them than a placeholder that reads as deliberate. The
 * gated preview is not rendered here either: it is the customer's real data, and this
 * branch exists precisely because there is someone else in the room.
 *
 * Not `PerformanceEmptyState`: its two CTAs ("Adjust filters" / "clear all") are wired to
 * the performance filters stores, which have nothing to do with a locked Optimize or
 * Simulations tab — it would offer the viewer two buttons that cannot help them.
 */
function NeutralPlaceholder({ className }: { className?: string }) {
  return (
    <section className={cn('flex min-h-[420px] items-center justify-center', className)}>
      <div
        className="flex w-[400px] max-w-[88%] flex-col items-center gap-1.5 rounded-2xl border border-neutral-100 bg-white px-8 py-7 text-center"
        role="status"
      >
        <h3 className="text-base font-semibold tracking-tight text-neutral-800">Nothing to show here</h3>
        <p className="mx-auto max-w-[320px] text-sm font-medium leading-snug text-neutral-500">
          This section isn&rsquo;t available in this view.
        </p>
      </div>
    </section>
  );
}

/**
 * The flagship treatment: the capability, running on the user's own portfolio, one blur
 * away.
 *
 * The conversion driver is the data being *theirs* — not the blur. So the panel keeps its
 * shape and its chart geometry, and only the affordances come out: a large green button
 * behind a blur still reads as a live action and invites a click that goes nowhere.
 */
export function UpsellTeaser({ feature, preview, title, description, className }: Props) {
  const suppressed = useUpsellSuppressed();
  const previewRef = React.useRef<HTMLDivElement>(null);

  // Called before any early return so hook order stays stable.
  useInert(previewRef, preview != null && !suppressed);

  if (suppressed) return <NeutralPlaceholder className={className} />;

  return (
    <section className={cn('relative min-h-[420px]', className)}>
      {preview ? (
        <div
          ref={previewRef}
          data-testid="upsell-preview"
          aria-hidden="true"
          className={cn('pointer-events-none select-none', PREVIEW_BLUR, PREVIEW_CLAMP, PREVIEW_HIDE_CONTROLS)}
        >
          {/* A crash inside the real panel must not take the upsell down with it. */}
          <ErrorBoundary>{preview}</ErrorBoundary>
        </div>
      ) : null}

      <div
        className={cn(
          'pointer-events-none flex items-center justify-center',
          preview
            ? cn(
                'absolute inset-0 bg-white/40 backdrop-blur-[1px]',
                // The scrim is the translucency, so this is where reduced-transparency
                // belongs: it makes the card more readable and the panel less. Reducing the
                // blur instead would expose the gated panel.
                '[@media(prefers-reduced-transparency:reduce)]:bg-white',
                '[@media(prefers-reduced-transparency:reduce)]:backdrop-blur-none',
              )
            : 'min-h-[420px]',
        )}
      >
        <UpsellCard feature={feature} title={title} description={description} />
      </div>
    </section>
  );
}

export default UpsellTeaser;
